import { getRuntimeConfig } from '@jane/shared/runtime-config';

export interface Config {
  actionCablePath: string;
  aeropayConsumerId: string;
  aeropayEnv: 'staging' | 'production';
  algoliaApiKey: string;
  algoliaAppId: string;
  algoliaEnv: string;
  algoliaUrl: string;
  apiPath: string;
  apiPathBase: string;
  apiPathV2: string;
  appVersion: string;
  appleClientId: string;
  branchIoKey: string;
  brandAnalyticsUrl: string;
  brandLaunchDarklyClientID: string;
  brandMixpanelToken: string;
  brandPortalUrl: string;
  brandServerUrl: string;
  brazeAPIKey: string;
  buildVersion: string;
  businessLaunchDarklyClientID: string;
  businessUrl: string;
  canPayV2RemotePaySDK: string;
  cartLimitDuplicateRuleUserIds: string;
  datadogEnv?: string;
  deployPublicPath: string;
  deployPublicPathWithoutUriScheme: string;
  dev: boolean;
  disableRecaptcha: boolean;
  dmEndpoint: string;
  dmMixpanelToken: string;
  dmSdkApiKey: string;
  dmServiceToken: string;
  facebookAppId: string;
  fakeAdStoreIds: string;
  features: string;
  fingerprintApiKey: string;
  fingerprintEndpoint: string;
  fingerprintScriptPath: string;
  framelessEmbedApiUrl: string;
  framelessEmbedMode: boolean;
  googleAnalyticsBusinessTrackingId: string;
  googleAnalyticsCustomerTrackingId: string;
  googleClientId: string;
  googleFontsKey: string;
  googlePlacesKey: string;
  herokuParentAppName: string;
  hostName: string;
  janeSupportManagerIds: string;
  kioskMixpanelCustomerToken: string;
  kioskRefreshTimeout: number;
  kioskTimeout: number;
  kioskV2Url: string;
  launchDarklyClientID: string;
  logMixpanelToConsole: string;
  mapboxAccessToken: string;
  mfaForceSMS: string;
  mixpanelCrmToken: string;
  mixpanelCustomerToken: string;
  mixpanelDomain: string;
  mixpanelUserSessionApiHost: string;
  monerisEnv: 'qa' | 'prod';
  monerisJsSdkUrl: string;
  recaptchaKey: string;
  retailTimeout: number;
  rootPath: string;
  rootsOnlyCustomRowStoreIds: string;
  roundingBetaStoreIds: string;
  ruleBasedCustomRowsStoreIds: string;
  session: { emailKey: string; tokenKey: string };
  squareApplicationId: string;
  squareWebPaymentsSdkUrl: string;
  streamChatApiToken: string;
  strongholdEnv: 'sandbox' | 'live';
  strongholdIntegrationId: string;
  test: boolean;
  trackJsApplication: string;
  trackJsDomain: string;
  trackJsToken: string;
  useEmailOverSMS: string;
}

const addBaseUrl = (relativePath: string) => {
  let baseUrl = process.env.NX_DEPLOY_PUBLIC_PATH ?? '';
  if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
  return `${baseUrl}${relativePath}`;
};

const addFramelessEmbedUrl = (relativePath: string) => {
  let baseUrl = process.env.NX_FRAMELESS_EMBED_API_URL ?? '';
  if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);

  let relativeNoApiPath = relativePath;
  if (relativeNoApiPath.startsWith('/api'))
    relativeNoApiPath = relativeNoApiPath.slice(4);

  return `${baseUrl}${relativeNoApiPath}`;
};

const getApiPath = (relativePath = '') => {
  const { partnerHostedStoreId, isFramelessEmbedMode } = getRuntimeConfig();
  const needsBaseUrl = partnerHostedStoreId && !isFramelessEmbedMode;

  if (isFramelessEmbedMode) return addFramelessEmbedUrl(relativePath);

  return needsBaseUrl ? addBaseUrl(relativePath) : relativePath;
};

export const config: Config = {
  [process.env.NODE_ENV]: true,
  actionCablePath: process.env.NX_ACTIONCABLE_URL,
  aeropayConsumerId: process.env.NX_AEROPAY_CONSUMER_ID,
  aeropayEnv: process.env.NX_AEROPAY_ENV,
  algoliaApiKey: process.env.NX_ALGOLIA_SEARCH_API_KEY,
  algoliaAppId: process.env.NX_ALGOLIA_APP_ID,
  algoliaEnv: process.env.NX_ALGOLIA_ENV,
  algoliaUrl: process.env.NX_ALGOLIA_URL,
  apiPath: getApiPath(process.env.NX_API_URL),
  apiPathBase: getApiPath(process.env.NX_API_URL_BASE),
  apiPathV2: getApiPath(process.env.NX_API_URL_V2),
  appVersion: process.env.NX_APP_VERSION,
  appleClientId: process.env.NX_APPLE_CLIENT_ID,
  branchIoKey: process.env.NX_BRANCH_IO_KEY,
  brandLaunchDarklyClientID: process.env.NX_BRAND_LAUNCH_DARKLY_CLIENT_ID,
  brandMixpanelToken: process.env.NX_BRAND_MIXPANEL_PROJECT_TOKEN,
  brandPortalUrl: process.env.NX_BRAND_PORTAL_URL,
  brandServerUrl: process.env.NX_BRAND_SERVER_URL,
  brazeAPIKey: process.env.NX_BRAZE_API_KEY,
  buildVersion: process.env.NX_BUILD_VERSION,
  businessLaunchDarklyClientID: process.env.NX_BUSINESS_LAUNCH_DARKLY_CLIENT_ID,
  businessUrl: process.env.NX_BUSINESS_URL,
  canPayV2RemotePaySDK: process.env.NX_CANPAYV2RP_JS_SDK_URL,
  cartLimitDuplicateRuleUserIds:
    process.env.NX_CART_LIMIT_DUPLICATE_RULE_USER_IDS,
  deployPublicPath: process.env.NX_DEPLOY_PUBLIC_PATH,
  deployPublicPathWithoutUriScheme:
    process.env.NX_DEPLOY_PUBLIC_PATH_WITHOUT_URI_SCHEME,
  datadogEnv: process.env.NX_DATADOG_ENV,
  dev: process.env.NX_DEV_MODE,
  disableRecaptcha: Boolean(process.env.NX_DISABLE_RECAPTCHA),
  dmEndpoint: process.env.NX_DM_ENDPOINT,
  dmMixpanelToken: process.env.NX_DM_MIXPANEL_CUSTOMER_TOKEN,
  dmSdkApiKey: process.env.NX_DM_SDK_API_KEY,
  dmServiceToken: process.env.NX_DM_SERVICE_TOKEN,
  env: process.env.NODE_ENV,
  facebookAppId: process.env.NX_FACEBOOK_APP_ID,
  fakeAdStoreIds: process.env.NX_FAKE_AD_STORE_IDS,
  features: process.env.NX_FEATURES,
  fingerprintApiKey: process.env.NX_FINGERPRINT_API_KEY,
  fingerprintEndpoint: process.env.NX_FINGERPRINT_ENDPOINT,
  fingerprintScriptPath: process.env.NX_FINGERPRINT_SCRIPT_PATH,
  framelessEmbedApiUrl: process.env.NX_FRAMELESS_EMBED_API_URL,
  framelessEmbedMode: getRuntimeConfig().isFramelessEmbedMode,
  googleAnalyticsBusinessTrackingId:
    process.env.NX_GOOGLE_ANALYTICS_BUSINESS_TRACKING_ID,
  googleAnalyticsCustomerTrackingId:
    process.env.NX_GOOGLE_ANALYTICS_CUSTOMER_TRACKING_ID,
  googleClientId: process.env.NX_GOOGLE_CLIENT_ID,
  googleFontsKey: process.env.NX_GOOGLE_FONTS_API_KEY,
  googlePlacesKey: process.env.NX_GOOGLE_PLACES_API_KEY,
  janeSupportManagerIds: process.env.NX_JANE_SUPPORT_MANAGER_IDS,
  kioskMixpanelCustomerToken: process.env.NX_KIOSK_MIXPANEL_CUSTOMER_TOKEN,
  kioskRefreshTimeout: process.env.NX_KIOSK_REFRESH_TIMEOUT,
  kioskTimeout: process.env.NX_KIOSK_TIMEOUT,
  kioskV2Url: process.env.NX_KIOSK_V2_URL,
  launchDarklyClientID: process.env.NX_LAUNCH_DARKLY_CLIENT_ID,
  logMixpanelToConsole: process.env.NX_LOG_MIXPANEL_TO_CONSOLE,
  mapboxAccessToken: process.env.NX_MAPBOX_ACCESS_TOKEN,
  mfaForceSMS: process.env.NX_MFA_FORCE_SMS,
  mixpanelCrmToken: process.env.NX_MIXPANEL_CRM_TOKEN,
  mixpanelCustomerToken: process.env.NX_MIXPANEL_CUSTOMER_TOKEN,
  mixpanelDomain: process.env.NX_MIXPANEL_DOMAIN,
  mixpanelUserSessionApiHost: process.env.NX_MIXPANEL_USER_SESSION_API_HOST,
  monerisEnv: process.env.NX_MONERIS_ENVIRONMENT,
  monerisJsSdkUrl: process.env.NX_MONERIS_JS_SDK_URL,
  recaptchaKey: process.env.NX_RECAPTCHA_KEY,
  retailTimeout: process.env.NX_RETAIL_TIMEOUT,
  rootsOnlyCustomRowStoreIds: process.env.NX_ROOTS_ONLY_CUSTOM_ROW_STORE_IDS,
  roundingBetaStoreIds: process.env.NX_ROUNDING_BETA_STORE_IDS,
  ruleBasedCustomRowsStoreIds: process.env.NX_RULE_BASED_CUSTOM_ROWS_STORE_IDS,
  squareApplicationId: process.env.NX_SQUARE_APPLICATION_ID,
  squareWebPaymentsSdkUrl: process.env.NX_SQUARE_WEB_PAYMENTS_SDK_URL,
  streamChatApiToken: process.env.NX_STREAM_CHAT_API_KEY,
  strongholdEnv: process.env.NX_STRONGHOLD_ENVIRONMENT,
  strongholdIntegrationId: process.env.NX_STRONGHOLD_INTEGRATION_ID,
  test: process.env.NX_TEST_MODE,
  trackJsApplication: process.env.NX_TRACKJS_APPLICATION,
  trackJsDomain: process.env.NX_TRACKJS_DOMAIN,
  trackJsToken: process.env.NX_TRACKJS_TOKEN,
  useEmailOverSMS: process.env.NX_USE_EMAIL_OVER_SMS,
  // TODO(wes): Remove type assertion, make properties nullable,
  // and update call sites to handle null/undefined appropriately
} as unknown as Config;

const FEATURES = (config.features || '')
  .split(',')
  .map((s: string) => s.trim());

export enum Feature {
  AeropayWhiteLabel = 'aeropayWhiteLabel',
  AppleSignIn = 'appleSignIn',
  CurbsidePickupNotify = 'curbsidePickupNotify',
  CustomRows = 'customRows',
  GoogleSignIn = 'googleSignIn',
  GuestCheckout = 'guestCheckout',
  OwnershipIdentification = 'ownershipIdentification',
  RootsOnlyCustomRows = 'rootsOnlyCustomRows',
  RoundingDisplay = 'roundingDisplay',
  Square = 'square',
  Stronghold = 'stronghold',
  StrongholdBusiness = 'strongholdBusiness',
  TwoWayMessaging = 'twoWayMessaging',
  VariableMaxOrders = 'variableMaxOrders',
}

export const isFeatureEnabled = (feature: Feature) => {
  const __TEST__ = config.test || process.env.NODE_ENV === 'test';
  return __TEST__ || FEATURES.includes(feature);
};
